import { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicrochip, faGear } from "@fortawesome/free-solid-svg-icons";
import StatusBar from '../../graphs/StatusBar';
import PercentageGauge from '../../graphs/PercentGauge';
import MultiLineChart from '../../graphs/MultiLineChart';

const SCORE_COLOR = "#008900";
const SCORE_DOMAIN = Object.freeze([0, 100]);
const SCORE_TICK_VALUES = Object.freeze([100, 0]);
const SCORE_MIDLINES = Object.freeze([{ value: 50, label: "50" }]);

function formatCount(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString();
}

function gaugeValue(value, fallback = 0) {
  if (value == null || Number.isNaN(Number(value))) return fallback;
  return Number(value);
}

export default function DataQualityPanel({
  id = "",
  title = "",
  chartRange,
  onTooltip,
  timeZone,
  metrics = null,
  loading = false,
  error = null,
}) {
  const scoreData = useMemo(() => {
    const series = metrics?.score_over_time;
    if (!Array.isArray(series) || series.length === 0) {
      return [];
    }
    return series.map((point) => ({
      date: new Date(`${point.date}T12:00:00Z`),
      value: Number(point.value ?? 0),
    }));
  }, [metrics]);

  const gauges = metrics?.gauges ?? {};
  const score = metrics?.score;
  const rowsProcessed = metrics?.rows_processed;
  const rowsFailed = metrics?.rows_failed;

  return (
    <div id={id} className="panel p-3 mb-5 dataQuality">
      <div className="header d-flex justify-content-between pb-3 mb-3">
        <div className="left d-flex gap-3">
          <span><FontAwesomeIcon icon={faMicrochip} /> {title}</span>
          {loading ? <span className="text-muted small">Loading…</span> : null}
          {error ? <span className="text-danger small">{error}</span> : null}
        </div>
        <div className="right d-flex align-items-stretch gap-2">
          <button type="button" aria-label="Settings"><FontAwesomeIcon icon={faGear} /></button>
        </div>
      </div>
      <div className="d-flex align-items-start gap-5">
        <div className="col-8">
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                <PercentageGauge
                    percentage={gaugeValue(gauges.data_availability_pct)}
                    label="Data Availability"
                />
                <PercentageGauge
                    percentage={gaugeValue(gauges.missing_values_pct)}
                    label="Missing Values"
                    invert={true}
                />
                <PercentageGauge
                    percentage={gaugeValue(gauges.out_of_range_pct)}
                    label="Out of Range Values"
                    invert={true}
                />
                <PercentageGauge
                    percentage={gaugeValue(gauges.state_frozen_pct)}
                    label="State/Frozen Tags"
                    invert={true}
                />
            </div>
            <MultiLineChart
              startDate={chartRange?.start}
              endDate={chartRange?.end}
              fixedYDomain
              color={SCORE_COLOR}
              domain={SCORE_DOMAIN}
              tickValues={SCORE_TICK_VALUES}
              midlines={SCORE_MIDLINES}
              unit=""
              tooltipUnit=""
              showPoints
              wrapperClassName="data-quality-over-time mt-3"
              headerLeft={<strong>DATA QUALITY OVER TIME</strong>}
              headerRight={<strong style={{ color: SCORE_COLOR }}>SCORE</strong>}
              data={scoreData}
              onTooltip={onTooltip}
              timeZone={timeZone}
            />
        </div>
        <div className="col">
          <div className="DataQualityMetrics">
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                <strong>DQ SCORE</strong>{' '}
                {score == null
                  ? '—'
                  : <StatusBar value={String(Math.round(score))} type="availability" />}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                <strong>ROWS PROCESSED</strong> {formatCount(rowsProcessed)}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                <strong>ROWS FAILED</strong> {formatCount(rowsFailed)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
